import React, { FC, Fragment, useState, useEffect } from 'react';
import styled from 'styled-components';

import { calculateShots, getLowestHandicap } from '../../../../utils/score-calculator';
import { Button } from '../../../common-styles/common-styles';
import { Course, Player, Match, PlayerResult, TournamentConfig } from '../../../../../database.schema';
import * as CSS from '../../match-entry-styles';
import { HandicapEntryConfirmation } from './handicap-entry-confirmation';
import { BACK_NINE_INDEX, FRONT_NINE_INDEX } from '../../../../app/constants';
import { useFetchPlayers } from '../../../../api/hooks/use-fetch-players';

const LeftBorderTD = styled.td<{ colour?: string | null }>`
  border-left: 2px solid;
  border-color: ${(props) => props.colour || 'white'};
  padding-left: 0.5rem;
`;
const RightBorderTD = styled.td<{ colour?: string | null }>`
  border-right: 2px solid;
  border-color: ${(props) => props.colour || 'white'};
`;
const TopBorderTD = styled.td<{ colour?: string | null }>`
  border-top: 2px solid;
  border-color: ${(props) => props.colour || 'white'};
`;
const BottomBorderTD = styled.td<{ colour?: string | null }>`
  border-bottom: 2px solid;
  border-color: ${(props) => props.colour || 'white'};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 1rem;
  position: relative;
`;

const CourseWrapper = styled.h3`
  margin: 1rem 0;
`;

type HandicapEntryProps = {
  config: TournamentConfig;
  editingMatch: boolean;
  startingHole: number;
  setRecordedMatch: (value: Match) => void;
  setShowHandicapEntry: (value: boolean) => void;
  recordedMatch: Match;
  course: Course;
  matchIndex: number;
  format: 'Pairs' | 'Singles';
};

export const CheckboxInput = styled.input`
  height: 1.25rem;
  width: 1.25rem;
  margin: 0 0.5rem;
`;

export const HandicapEntry: FC<HandicapEntryProps> = ({
  config,
  editingMatch,
  startingHole,
  setRecordedMatch,
  recordedMatch,
  course,
  setShowHandicapEntry,
  format,
}): JSX.Element => {
  const holeIndexes = startingHole === 1 ? [...FRONT_NINE_INDEX, ...BACK_NINE_INDEX] : [...BACK_NINE_INDEX, ...FRONT_NINE_INDEX];
  const { data } = useFetchPlayers();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [recordedMatchStore, setRecordedMatchStore] = useState<Match>(recordedMatch);
  const [lowestHandicap, setLowestHandicap] = useState<number>(100);

  const adjustCourseScoresToHandicap = (handicap: number | undefined): number[] | undefined => {
    if (course && handicap) {
      return holeIndexes.map((holeIndex: number) => {
        return course.scoreCard[holeIndex] + calculateShots(handicap, course.strokeIndex[holeIndex]);
      });
    }
    return undefined;
  };

  const players = data as Array<Player>;
  useEffect(() => {
    const newRecordedMatch = { ...recordedMatchStore };
    newRecordedMatch.team1.players.forEach((player: PlayerResult, index: number) => {
      if (player.round) {
        const shotsReceived = getMatchShotsReceived(player.round.individualHandicap);
        newRecordedMatch.team1.players[index] = { ...player, round: { ...player.round, shotsReceived } };
      }
    });
    newRecordedMatch.team2.players.forEach((player: PlayerResult, index: number) => {
      if (player.round) {
        const shotsReceived = getMatchShotsReceived(player.round.individualHandicap);
        newRecordedMatch.team2.players[index] = { ...player, round: { ...player.round, shotsReceived } };
      }
    });
    setRecordedMatchStore(newRecordedMatch);
  }, [lowestHandicap]);

  useEffect(() => {
    if (editingMatch) {
      setLowestHandicap(getLowestHandicap(recordedMatch));
    } else {
      const team1Players: PlayerResult[] = [];
      const team2Players: PlayerResult[] = [];
      recordedMatch.team1.players.forEach((player: PlayerResult) => {
        if (player.round) {
          const handicap = player.round.individualHandicap === -1 ? 18 : player.round.courseHandicap;
          // const individualHandicap = player.round.courseHandicap === -1 ? Math.round(18 * 0.95) : player.round.individualHandicap;
          const adjustedCourseScoreCard = adjustCourseScoresToHandicap(Number(handicap));
          const longestDrive = player.longestDrive ? player.longestDrive : 0;
          const nearestThePin = player.nearestThePin ? player.nearestThePin : 0;
          const nearestThePinIn2 = player.nearestThePinIn2 ? player.nearestThePinIn2 : 0;
          const shotsReceived = getMatchShotsReceived(player.round.individualHandicap);
          if (adjustedCourseScoreCard) {
            team1Players.push({
              ...player,
              longestDrive,
              nearestThePin,
              nearestThePinIn2,
              round: { ...player.round, courseHandicap: 0, individualHandicap: handicap, shotsReceived, scoreCard: adjustedCourseScoreCard },
            });
          }
        }
      });
      recordedMatch.team2.players.forEach((player: PlayerResult) => {
        if (player.round) {
          const handicap = player.round.individualHandicap === -1 ? 18 : player.round.individualHandicap;
          // const individualHandicap = player.round.courseHandicap === -1 ? Math.round(18 * 0.95) : player.round.individualHandicap;
          const adjustedCourseScoreCard = adjustCourseScoresToHandicap(Number(handicap));
          const longestDrive = player.longestDrive ? player.longestDrive : 0;
          const nearestThePin = player.nearestThePin ? player.nearestThePin : 0;
          const nearestThePinIn2 = player.nearestThePinIn2 ? player.nearestThePinIn2 : 0;
          const shotsReceived = getMatchShotsReceived(player.round.individualHandicap);
          if (adjustedCourseScoreCard) {
            team2Players.push({
              ...player,
              longestDrive,
              nearestThePin,
              nearestThePinIn2,
              round: { ...player.round, courseHandicap: 0, individualHandicap: handicap, shotsReceived, scoreCard: adjustedCourseScoreCard },
            });
          }
        }
      });
      const newRecordedMatch = {
        ...recordedMatch,
        team1: { players: team1Players, points: 0 as 0 | 1 | 0.5 },
        team2: { players: team2Players, points: 0 as 0 | 1 | 0.5 },
      };
      setRecordedMatchStore(newRecordedMatch);
      setLowestHandicap(getLowestHandicap(newRecordedMatch));
    }
  }, [recordedMatch, editingMatch]);

  const getMatchShotsReceived = (handicap: number) => {
    const difference = handicap - lowestHandicap;
    return format.toLowerCase() === 'pairs' ? Math.round(difference * 0.9) : difference;
  };

  const allHandicapsHaveBeenEntered = () => {
    return (
      recordedMatchStore.team1.players.every((player: PlayerResult) => player.round.individualHandicap > 0) &&
      recordedMatchStore.team2.players.every((player: PlayerResult) => player.round.individualHandicap > 0)
    );
  };
  const getPlayer = (playerId: number) => {
    return players.find((player: Player) => player.id === playerId);
  };

  const setHandicapForPlayer = (index: number, handicap: number, team: string) => {
    if (handicap && course) {
      var newRecordedMatch: Match;
      const adjustedCourseScoreCard = adjustCourseScoresToHandicap(Number(handicap));

      const shotsReceived = getMatchShotsReceived(handicap);
      if (adjustedCourseScoreCard) {
        if (team === 'team1') {
          const newTeam1Rounds = [...recordedMatchStore.team1.players];
          const newRound: PlayerResult = {
            ...newTeam1Rounds[index],
            round: {
              scoreCard: adjustedCourseScoreCard,
              courseHandicap: 0,
              individualHandicap: handicap,
              shotsReceived,
              playerId: -1,
              points: 0,
            },
          };
          newTeam1Rounds.splice(index, 1, { ...newRound });
          newRecordedMatch = { ...recordedMatchStore, team1: { ...recordedMatch.team1, players: newTeam1Rounds } };
        } else {
          const newTeam2Rounds = [...recordedMatchStore.team2.players];
          const newRound = {
            ...newTeam2Rounds[index],
            round: {
              scoreCard: adjustedCourseScoreCard,
              courseHandicap: 0,
              individualHandicap: handicap,
              shotsReceived,
              playerId: -1,
              points: 0,
            },
          };
          newTeam2Rounds.splice(index, 1, { ...newTeam2Rounds[index], ...newRound });
          newRecordedMatch = { ...recordedMatchStore, team2: { ...recordedMatch.team2, players: newTeam2Rounds } };
        }
        setRecordedMatchStore(newRecordedMatch);
        setLowestHandicap(getLowestHandicap(newRecordedMatch));
      }
    }
  };

  const setLongestDriveForPlayer = (index: number, longestDrive: number, team: string) => {
    var newRecordedMatch: Match;
    if (team === 'team1') {
      const newTeam1Rounds = [...recordedMatchStore.team1.players];
      const newRound: PlayerResult = {
        ...newTeam1Rounds[index],
        longestDrive,
      };
      newTeam1Rounds.splice(index, 1, { ...newRound });
      newRecordedMatch = { ...recordedMatchStore, team1: { ...recordedMatch.team1, players: newTeam1Rounds } };
    } else {
      const newTeam2Rounds = [...recordedMatchStore.team2.players];
      const newRound = {
        ...newTeam2Rounds[index],
        longestDrive,
      };
      newTeam2Rounds.splice(index, 1, { ...newTeam2Rounds[index], ...newRound });
      newRecordedMatch = { ...recordedMatchStore, team2: { ...recordedMatch.team2, players: newTeam2Rounds } };
    }
    setRecordedMatchStore(newRecordedMatch);
  };

  const setNearestThePinForPlayer = (index: number, nearestThePin: number, team: string) => {
    var newRecordedMatch: Match;
    if (team === 'team1') {
      const newTeam1Rounds = [...recordedMatchStore.team1.players];
      const newRound: PlayerResult = {
        ...newTeam1Rounds[index],
        nearestThePin,
      };
      newTeam1Rounds.splice(index, 1, { ...newRound });
      newRecordedMatch = { ...recordedMatchStore, team1: { ...recordedMatch.team1, players: newTeam1Rounds } };
    } else {
      const newTeam2Rounds = [...recordedMatchStore.team2.players];
      const newRound = {
        ...newTeam2Rounds[index],
        nearestThePin,
      };
      newTeam2Rounds.splice(index, 1, { ...newTeam2Rounds[index], ...newRound });
      newRecordedMatch = { ...recordedMatchStore, team2: { ...recordedMatch.team2, players: newTeam2Rounds } };
    }
    setRecordedMatchStore(newRecordedMatch);
  };

  const setNearestThePinIn2ForPlayer = (index: number, nearestThePinIn2: number, team: string) => {
    var newRecordedMatch: Match;
    if (team === 'team1') {
      const newTeam1Rounds = [...recordedMatchStore.team1.players];
      const newRound: PlayerResult = {
        ...newTeam1Rounds[index],
        nearestThePinIn2,
      };
      newTeam1Rounds.splice(index, 1, { ...newRound });
      newRecordedMatch = { ...recordedMatchStore, team1: { ...recordedMatch.team1, players: newTeam1Rounds } };
    } else {
      const newTeam2Rounds = [...recordedMatchStore.team2.players];
      const newRound = {
        ...newTeam2Rounds[index],
        nearestThePinIn2,
      };
      newTeam2Rounds.splice(index, 1, { ...newTeam2Rounds[index], ...newRound });
      newRecordedMatch = { ...recordedMatchStore, team2: { ...recordedMatch.team2, players: newTeam2Rounds } };
    }
    setRecordedMatchStore(newRecordedMatch);
  };

  const setShotsReceivedForPlayer = (index: number, shotsReceived: number, team: string) => {
    if (shotsReceived && course) {
      var newRecordedMatch: Match;
      if (team === 'team1') {
        const newTeam1Rounds = [...recordedMatchStore.team1.players];
        const newRound: PlayerResult = {
          ...newTeam1Rounds[index],
          round: {
            scoreCard: newTeam1Rounds[index].round.scoreCard,
            courseHandicap: newTeam1Rounds[index].round.courseHandicap,
            individualHandicap: newTeam1Rounds[index].round.individualHandicap,
            shotsReceived: shotsReceived,
            playerId: -1,
            points: 0,
          },
        };
        newTeam1Rounds.splice(index, 1, { ...newRound });
        newRecordedMatch = { ...recordedMatchStore, team1: { ...recordedMatch.team1, players: newTeam1Rounds } };
      } else {
        const newTeam2Rounds = [...recordedMatchStore.team2.players];
        const newRound = {
          ...newTeam2Rounds[index],
          round: {
            scoreCard: newTeam2Rounds[index].round.scoreCard,
            courseHandicap: newTeam2Rounds[index].round.courseHandicap,
            individualHandicap: newTeam2Rounds[index].round.individualHandicap,
            shotsReceived: shotsReceived,
            playerId: -1,
            points: 0,
          },
        };
        newTeam2Rounds.splice(index, 1, { ...newTeam2Rounds[index], ...newRound });
        newRecordedMatch = { ...recordedMatchStore, team2: { ...recordedMatch.team2, players: newTeam2Rounds } };
      }
      setRecordedMatchStore(newRecordedMatch);
    }
  };

  const decrementShotsReceived = (index: number, shotsReceived: number, team: string) => {
    const newHandicap = shotsReceived - 1;
    setShotsReceivedForPlayer(index, newHandicap, team);
  };
  const incrementShotsReceived = (index: number, shotsReceived: number, team: string) => {
    const newHandicap = shotsReceived + 1;
    setShotsReceivedForPlayer(index, newHandicap, team);
  };
  const decrementIndividualHandicap = (index: number, handicap: number, team: string) => {
    const newHandicap = handicap - 1;
    setHandicapForPlayer(index, newHandicap, team);
  };
  const incrementIndividualHandicap = (index: number, handicap: number, team: string) => {
    const newHandicap = handicap + 1;
    setHandicapForPlayer(index, newHandicap, team);
  };
  if (showConfirm) {
    return (
      <div>
        <HandicapEntryConfirmation recordedMatch={recordedMatchStore} course={course} players={players} format={format} />
        <CSS.NavigationWrapper>
          <CSS.FlexRow>
            <CSS.NavigationButton
              onClick={() => {
                setShowConfirm(false);
              }}
            >
              Back
            </CSS.NavigationButton>
            <CSS.NavigationButton
              data-testid="confirm-button"
              onClick={() => {
                setRecordedMatch({ ...recordedMatchStore });
                setShowHandicapEntry(false);
              }}
            >
              Confirm
            </CSS.NavigationButton>
          </CSS.FlexRow>
        </CSS.NavigationWrapper>
      </div>
    );
  }

  return (
    <Fragment>
      <h2>Handicap Entry</h2>
      <CourseWrapper>Course: {course.name}</CourseWrapper>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <th>Player</th>
            <th>Stableford</th>
            <th>Shots</th>
            {config.showLongestDrive && <th>LD</th>}
            {config.showNearestThePin && <th>NTP&nbsp;&nbsp;</th>}
            {config.showNearestThePinIn2 && <th>NTPIn2&nbsp;&nbsp;</th>}
            <th></th>
          </tr>
          <tr>
            <BottomBorderTD colSpan={7} style={{ borderBottom: '2px red solid' }}></BottomBorderTD>
          </tr>
          {recordedMatchStore.team1.players.map((player: PlayerResult, index: number) => (
            <tr key={`${player.playerId}`}>
              <LeftBorderTD colour="red">{getPlayer(player.playerId ?? 1)?.nickName}</LeftBorderTD>
              <td>
                <CSS.CrementWrapper>
                  <CSS.FlexRow>
                    <CSS.CrementButton data-testid={`t1-decrement-${index}`} onClick={() => decrementIndividualHandicap(index, player.round.individualHandicap, 'team1')}>
                      -
                    </CSS.CrementButton>
                    <CSS.HoleInfo>{player.round.individualHandicap}</CSS.HoleInfo>
                    <CSS.CrementButton data-testid={`t1-increment-${index}`} onClick={() => incrementIndividualHandicap(index, player.round.individualHandicap, 'team1')}>
                      +
                    </CSS.CrementButton>
                  </CSS.FlexRow>
                </CSS.CrementWrapper>
              </td>
              {player.round.shotsReceived > 0 ? (
                <td>
                  <CSS.CrementWrapper>
                    <CSS.FlexRow>
                      <CSS.CrementButton data-testid={`t1-decrement-shots-${index}`} onClick={() => decrementShotsReceived(index, player.round.shotsReceived, 'team1')}>
                        -
                      </CSS.CrementButton>
                      <CSS.HoleInfo>{player.round.shotsReceived}</CSS.HoleInfo>
                      <CSS.CrementButton data-testid={`t1-increment-shots-${index}`} onClick={() => incrementShotsReceived(index, player.round.shotsReceived, 'team1')}>
                        +
                      </CSS.CrementButton>
                    </CSS.FlexRow>
                  </CSS.CrementWrapper>
                </td>
              ) : (
                <td>
                  <CSS.PlayerShots>-</CSS.PlayerShots>
                </td>
              )}
              {config.showLongestDrive && (
                <td>
                  <CheckboxInput
                    type="checkbox"
                    name="longestDrive"
                    id="tier-points"
                    data-testid={`t1-longest-drive-checkbox-${index}`}
                    checked={player.longestDrive > 0}
                    onChange={() => setLongestDriveForPlayer(index, player.longestDrive === 0 ? 1 : 0, 'team1')}
                  />
                </td>
              )}
              {config.showNearestThePin && (
                <td>
                  <CheckboxInput
                    type="checkbox"
                    name="nearestThePin"
                    id="tier-points"
                    data-testid={`t1-nearest-the-pin-checkbox-${index}`}
                    checked={player.nearestThePin > 0}
                    onChange={() => setNearestThePinForPlayer(index, player.nearestThePin === 0 ? 1 : 0, 'team1')}
                  />
                </td>
              )}
              {config.showNearestThePinIn2 && (
                <td>
                  <CheckboxInput
                    type="checkbox"
                    name="nearestThePinIn2"
                    id="tier-points"
                    data-testid={`t1-nearest-the-pin-in-2-checkbox-${index}`}
                    checked={player.nearestThePinIn2 > 0}
                    onChange={() => setNearestThePinIn2ForPlayer(index, player.nearestThePinIn2 === 0 ? 1 : 0, 'team1')}
                  />
                </td>
              )}
              <RightBorderTD colour="red"></RightBorderTD>
            </tr>
          ))}
          <tr>
            <TopBorderTD colour="red" colSpan={6}></TopBorderTD>
          </tr>
          <tr>
            <BottomBorderTD colour="blue" colSpan={6}></BottomBorderTD>
          </tr>
          {recordedMatchStore.team2.players.map((player: PlayerResult, index: number) => (
            <tr key={`${player.playerId}`}>
              <LeftBorderTD colour="blue">{getPlayer(player.playerId)?.nickName}</LeftBorderTD>
              <td>
                <CSS.CrementWrapper>
                  <CSS.FlexRow>
                    <CSS.CrementButton data-testid={`t2-decrement-${index}`} onClick={() => decrementIndividualHandicap(index, player.round.individualHandicap, 'team2')}>
                      -
                    </CSS.CrementButton>
                    <CSS.HoleInfo>{player.round.individualHandicap}</CSS.HoleInfo>
                    <CSS.CrementButton data-testid={`t2-increment-${index}`} onClick={() => incrementIndividualHandicap(index, player.round.individualHandicap, 'team2')}>
                      +
                    </CSS.CrementButton>
                  </CSS.FlexRow>
                </CSS.CrementWrapper>
              </td>
              {player.round.shotsReceived > 0 ? (
                <td>
                  <CSS.CrementWrapper>
                    <CSS.FlexRow>
                      <CSS.CrementButton data-testid={`t2-decrement-shots-${index}`} onClick={() => decrementShotsReceived(index, player.round.shotsReceived, 'team2')}>
                        -
                      </CSS.CrementButton>
                      <CSS.HoleInfo>{player.round.shotsReceived}</CSS.HoleInfo>
                      <CSS.CrementButton data-testid={`t2-increment-shots-${index}`} onClick={() => incrementShotsReceived(index, player.round.shotsReceived, 'team2')}>
                        +
                      </CSS.CrementButton>
                    </CSS.FlexRow>
                  </CSS.CrementWrapper>
                </td>
              ) : (
                <td>
                  <CSS.PlayerShots>-</CSS.PlayerShots>
                </td>
              )}
              {config.showLongestDrive && (
                <td>
                  <CheckboxInput
                    type="checkbox"
                    name="longestDrive"
                    id="tier-points"
                    data-testid={`t2-longest-drive-checkbox-${index}`}
                    checked={player.longestDrive > 0}
                    onChange={() => setLongestDriveForPlayer(index, player.longestDrive === 0 ? 1 : 0, 'team2')}
                  />
                </td>
              )}
              {config.showNearestThePin && (
                <td>
                  <CheckboxInput
                    type="checkbox"
                    name="nearestThePin"
                    id="tier-points"
                    data-testid={`t2-nearest-the-pin-checkbox-${index}`}
                    checked={player.nearestThePin > 0}
                    onChange={() => setNearestThePinForPlayer(index, player.nearestThePin === 0 ? 1 : 0, 'team2')}
                  />
                </td>
              )}
              {config.showNearestThePinIn2 && (
                <td>
                  <CheckboxInput
                    type="checkbox"
                    name="nearestThePin"
                    id="tier-points"
                    data-testid={`t2-nearest-the-pin-in-2-checkbox-${index}`}
                    checked={player.nearestThePinIn2 > 0}
                    onChange={() => setNearestThePinIn2ForPlayer(index, player.nearestThePinIn2 === 0 ? 1 : 0, 'team2')}
                  />
                </td>
              )}
              <RightBorderTD colour="blue"></RightBorderTD>
            </tr>
          ))}
          <tr>
            <TopBorderTD colour="blue" colSpan={6}></TopBorderTD>
          </tr>
        </tbody>
      </table>
      <div>Shots: {format.toLowerCase() === 'pairs' ? `Fourball 90%` : `singles 100%`} of difference</div>
      <div>Stableford 95% of handicap</div>
      <ButtonWrapper>
        <Button data-testid="continue-button" onClick={() => setShowConfirm(true)} disabled={!Boolean(allHandicapsHaveBeenEntered())}>
          Continue
        </Button>
      </ButtonWrapper>
    </Fragment>
  );
};
